import React, { useEffect, useRef, useState } from 'react';

import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { IOffer } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import { useOffer } from 'components/offer/use-offer';
import RestaurantRedemptionModal from 'components/offer-redemption-modal/redemption-modal';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import useDialogModal, { DialogCb } from 'hooks/use-dialog-modal';
import { useForceStoreSelectionFlagged } from 'hooks/use-force-store-selection';
import LoyaltyReadyToRedeemModal from 'pages/loyalty/loyalty-ready-to-redeem-modal';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RedemptionMethod, useOffersContext } from 'state/offers';
import { useServiceModeContext } from 'state/service-mode';

import { IDetailsPanelErrorProps } from './details-panel-error';
import { DetailsPanelView } from './details-panel-view';
import OfferUpsellModal from './offer-upsell-modal';
import { useOffersUIContext } from './offers-ui-context';
import { IAttemptOfferRedemptionArgs } from './types';
import { MOBILE_REDEMPTION, RESTAURANT_REDEMPTION, useOfferDetails } from './use-offer-details';
import { useOfferRedemptionFlow } from './use-offer-redemption-flow';

export interface IDetailsPanelProps extends IDetailsPanelErrorProps {
  offer: IOffer;
  offersInCooldown: boolean;
}

export const DetailsPanel: React.FC<IDetailsPanelProps> = ({ offer, offersInCooldown }) => {
  const {
    authenticated,
    updateDetailsPanelContentHeight,
    previewOfferFeedback: offerFeedback,
    offers,
    togglePromoCodeOfferRedeemable,
  } = useOffersUIContext();
  const offersCount = offers.length;
  const offerIndex = offers.findIndex(o => o._id === offer._id);

  const { isDelivery } = useServiceModeContext();
  useForceStoreSelectionFlagged();

  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();

  const enableImprovedSimplifiedRedeemInRestaurantStaticOffers = useFlag(
    LaunchDarklyFlag.ENABLE_IMPROVED_SIMPLIFIED_REDEEM_IN_RESTAURANT_STATIC_OFFERS
  );

  const {
    loginRequiredForRedemption,
    mobileRedemptionAvailable,
    disableInRestaurantOfferRedemptions,
    disableMobileOfferRedemptions,
    offerAvailableRightNow,
    redemptionErrorMessage,
    restaurantRedemptionAvailable,
    showRestaurantRedemptionButton,
    showMobileRedemptionButton,
    restaurantRedemptionButtonUiType,
    showSignUpToRedeem,
    noPossibleRedemption,
  } = useOfferDetails({
    authenticated,
    offer,
    offerIndex,
    offersCount,
  });

  const { onSelectRestaurantRedemption, selectOfferById } = useOffersContext();
  const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();

  const {
    attemptOfferRedemption,
    onConfirmSwitchOffers,
    onConfirmSwitchServiceMode,
    onCancelSwitchOffers,
    onCancelSwitchServiceMode,
    onConfirmReadyToRedeem,
    onCancelReadyToRedeem,
    onDismissRestaurantRedemption,
    showConfirmSwitchOffersDialog,
    showReadyToRedeemDialog,
    showRestaurantRedemptionModal,
    showSwitchServiceModeDialog,
    restaurantRedemptionStartTime,
    loadingOfferConfigs,
    redemptionMethod,
  } = useOfferRedemptionFlow({
    offer,
    authenticated,
    offerAvailableRightNow,
    mobileRedemptionAvailable,
    restaurantRedemptionAvailable,
    loginRequiredForRedemption,
  });
  const [showUpsellOffer, setShowUpsellOffer] = useState(false);
  const wasUpsellOfferDiscarded = useRef(false);

  const [ReadyToRedeemDialog, openReadyToRedeemDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Confirm Ready to Redeem',
    Component: enableImprovedSimplifiedRedeemInRestaurantStaticOffers
      ? LoyaltyReadyToRedeemModal
      : undefined,
    onConfirm: onConfirmReadyToRedeem as DialogCb<object>,
    onDismiss: onCancelReadyToRedeem as DialogCb<object>,
    showCancel: enableImprovedSimplifiedRedeemInRestaurantStaticOffers,
  });

  useEffect(() => {
    if (showReadyToRedeemDialog) {
      openReadyToRedeemDialog();
    }
  }, [openReadyToRedeemDialog, showReadyToRedeemDialog]);

  const { endingIn, platforms } = useOffer({ offer, offerFeedback });

  const enableOfferBadges = useFlag(LaunchDarklyFlag.ENABLE_OFFER_BADGES);
  const enableLoyaltyRedeemAllStores = useFlag(
    LaunchDarklyFlag.ENABLE_LOYALTY_REDEEM_IN_RESTURANT_SCREEN_IN_ALL_STORES
  );
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const handleOfferRedeem = React.useCallback(
    ({
      redemptionMethod: method,
      bypassCheckForAnotherOffer = false,
    }: IAttemptOfferRedemptionArgs) => {
      if (!wasUpsellOfferDiscarded.current && offer.upsellOptions?.length) {
        setShowUpsellOffer(true);
        return;
      }

      // Show confirmation modal if it's a legacy offer and is redeemed in restaurant
      if (enableLoyaltyRedeemAllStores && method === RedemptionMethod.restaurant) {
        selectOfferById(offer._id);
        onSelectRestaurantRedemption();
        openReadyToRedeemDialog();
        return;
      }

      attemptOfferRedemption({ redemptionMethod: method, bypassCheckForAnotherOffer });
    },
    [
      attemptOfferRedemption,
      enableLoyaltyRedeemAllStores,
      offer._id,
      offer.upsellOptions,
      onSelectRestaurantRedemption,
      openReadyToRedeemDialog,
      selectOfferById,
    ]
  );
  const handleUpsellOfferDiscard = React.useCallback(() => {
    setShowUpsellOffer(false);
    wasUpsellOfferDiscarded.current = true;
  }, [setShowUpsellOffer, wasUpsellOfferDiscarded]);

  const shouldDisableMobileRedemption =
    offersInCooldown ||
    disableMobileOfferRedemptions ||
    loadingOfferConfigs ||
    !MOBILE_REDEMPTION.includes(redemptionMethod || '');

  const shouldDisableRestaurantRedemption =
    offersInCooldown ||
    disableInRestaurantOfferRedemptions ||
    loadingOfferConfigs ||
    !RESTAURANT_REDEMPTION.includes(redemptionMethod || '');

  return (
    <>
      <DetailsPanelView
        togglePromoCodeOfferRedeemable={togglePromoCodeOfferRedeemable}
        offer={offer}
        onRedeemOffer={handleOfferRedeem}
        showRestaurantRedemptionButton={showRestaurantRedemptionButton}
        disableInRestaurantOfferRedemptions={shouldDisableRestaurantRedemption}
        disableMobileOfferRedemptions={shouldDisableMobileRedemption}
        showMobileRedemptionButton={showMobileRedemptionButton}
        redemptionErrorMessage={redemptionErrorMessage}
        enableOfferBadges={enableOfferBadges}
        endingIn={endingIn}
        platforms={platforms}
        updateDetailsPanelContentHeight={updateDetailsPanelContentHeight ?? noop}
        showSignUpToRedeem={showSignUpToRedeem}
        restaurantRedemptionButtonUiType={restaurantRedemptionButtonUiType}
        noPossibleRedemption={noPossibleRedemption}
        offerConfigLoading={loadingOfferConfigs}
        enableFavoriteOffers={enableFavoriteOffers && isAuthenticated()}
        favorited={isFavorite(offer._id)}
        favoriteLoading={isOfferLoading(offer._id)}
        handleToggleFavorite={toggleFavorite}
      />
      {showConfirmSwitchOffersDialog && (
        <ConfirmDialog
          heading={formatMessage({ id: 'oneOfferPerOrder' })}
          body={formatMessage({ id: 'replaceOffer' })}
          confirmLabel={formatMessage({ id: 'yes' })}
          cancelLabel={formatMessage({ id: 'no' })}
          onConfirm={onConfirmSwitchOffers}
          onCancel={onCancelSwitchOffers}
          onDismiss={onCancelSwitchOffers}
          modalAppearanceEventMessage="Confirm Replace Coupon"
        />
      )}
      {showSwitchServiceModeDialog && (
        <ConfirmDialog
          heading={formatMessage({
            id: isDelivery
              ? 'switchServiceModeToPickupHeader'
              : 'switchServiceModeToDeliveryHeader',
          })}
          body={formatMessage({
            id: isDelivery ? 'switchServiceModeToPickupBody' : 'switchServiceModeToDeliveryBody',
          })}
          confirmLabel={formatMessage({
            id: isDelivery
              ? 'switchServiceModeToPickupButton'
              : 'switchServiceModeToDeliveryButton',
          })}
          cancelLabel={formatMessage({ id: 'cancel' })}
          onConfirm={onConfirmSwitchServiceMode}
          onCancel={onCancelSwitchServiceMode}
          onDismiss={onCancelSwitchServiceMode}
          modalAppearanceEventMessage="Confirm Change Service Mode"
        />
      )}
      <ReadyToRedeemDialog
        data-testid="ready-to-redeem-dialog"
        heading={formatMessage({ id: 'couponWillExpire' })}
        body={formatMessage({ id: 'confirmDialogBody' })}
        buttonLabel={formatMessage({ id: 'redeem' })}
        showCloseButton
      />
      {showUpsellOffer && offer.upsellOptions?.length ? (
        <OfferUpsellModal
          parentId={offer._id}
          upsellOptions={offer.upsellOptions}
          onDismiss={handleUpsellOfferDiscard}
        />
      ) : null}
      {showRestaurantRedemptionModal && (
        <RestaurantRedemptionModal
          selectedOffer={offer}
          redemptionStartTime={restaurantRedemptionStartTime}
          onDismiss={onDismissRestaurantRedemption}
        />
      )}
    </>
  );
};
